import { useMemo } from 'react';
import { motion, useTransform } from 'framer-motion';
import { Camera, Car, Mic, Film, Sun, Smartphone, Share2, FileText, Bell, User, Sparkles, Music, Scissors, Palette, PenLine, AudioLines, UserRound, Layers } from 'lucide-react';
import { useSceneTime, seg, EASE } from '../engine/sceneTime';
import { Words, Fade, Eyebrow } from '../components/Kinetic';
import { Vid, PhoneFrame } from '../components/Media';
import { IMAGES, VIDEOS } from '../assets';

// ── 11–13 CUSTOMER JOURNEY (one continuous road) ───────────────
const PX = 110;
const X0 = 320;
const ROAD_Y = 660;
const yAt = (x) => ROAD_Y + Math.sin(x / 430) * 30;
const STEM = 50;
const STEM_DOWN = 40;
const PHONE_W = 180;
const PHONE_H = Math.round((PHONE_W - 22) * (16 / 9) + 22);

// Every media milestone is a vertical video inside a phone mock-up as it scrolls past.
export const MILESTONES = [
  { time: 2.5, label: 'Pre-Valuation Video', media: VIDEOS.postValuation, side: 'up', stage: 0 },
  { time: 7, label: 'Valuation', side: 'down', stage: 0 },
  { time: 11, label: 'Instruction', side: 'up', stage: 0 },
  { time: 15, label: 'NicheCom visit', visit: true, side: 'up', stage: 0 },
  { time: 19, label: 'Property Video', media: VIDEOS.propertyTour, side: 'down', stage: 0 },
  { time: 22.5, label: 'Sneak Peek', media: VIDEOS.jsSneakPeek, side: 'up', stage: 1, hub: true },
  { time: 26, label: 'Facebook', side: 'up', stage: 1, branch: -1 },
  { time: 28.5, label: 'Instagram', side: 'down', stage: 1, branch: 1 },
  { time: 32.5, label: 'PresenTour', media: VIDEOS.presentourExample, side: 'up', stage: 1 },
  { time: 36.5, label: 'Just Listed Reel', media: VIDEOS.jsJustListed, side: 'down', stage: 1 },
  { time: 42.5, label: 'Post-Viewing Video', media: VIDEOS.jsPostViewing, side: 'up', stage: 2 },
  { time: 47, label: 'Feedback Request', side: 'down', stage: 2 },
  { time: 51.5, label: 'Price Reduction', media: VIDEOS.jsPriceReduction, side: 'up', stage: 2 },
  { time: 56, label: 'Sold', media: VIDEOS.jsSold, side: 'down', stage: 2, sold: true },
].map((m) => ({ ...m, x: X0 + m.time * PX }));

const ROAD_LEN = X0 + 62 * PX + 800;
const ROAD_PATH = (() => {
  const pts = [];
  for (let x = -400; x <= ROAD_LEN; x += 40) pts.push(`${x},${yAt(x).toFixed(1)}`);
  return `M${pts.join(' L')}`;
})();

const easeOut = (p) => 1 - Math.pow(1 - p, 3);
const CARD_W = 320; const CARD_H = 200;
const VISIT_CHIPS = ['Photos', 'Floorplan', 'Video', 'Drone', 'EPC'];

function DroneOutline() {
  return (
    <svg width={84} height={46} viewBox="0 0 84 46" fill="none" stroke="#C6EE5A" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x={32} y={19} width={20} height={11} rx={3.5} />
      <path d="M32 24 L14 14 M52 24 L70 14 M32 24 L14 34 M52 24 L70 34" />
      <ellipse className="pk-rotor" cx={14} cy={14} rx={11} ry={3} />
      <ellipse className="pk-rotor" cx={70} cy={14} rx={11} ry={3} />
      <ellipse className="pk-rotor" cx={14} cy={34} rx={11} ry={3} />
      <ellipse className="pk-rotor" cx={70} cy={34} rx={11} ry={3} />
      <circle cx={42} cy={33} r={2.2} fill="#C6EE5A" stroke="none" />
    </svg>
  );
}

// NicheCom arrives on the road, the photographer is in the house, then the drone goes up.
function VisitMilestone({ m, now, photographer }) {
  const driveP = easeOut(seg(now, m.time - 3, m.time));
  const carX = -900 + 900 * driveP;
  const carY = yAt(m.x + carX) - yAt(m.x);
  const carOn = now >= m.time - 3.2;
  const photo = now >= m.time + 0.4;
  const flash = now >= m.time + 0.4 && now < m.time + 0.75;
  const drone = now >= m.time + 1.8;
  const chipsAt = m.time + 2.8;
  const cardTop = -STEM - 16 - CARD_H;
  return (
    <>
      <motion.div className="pk-abs pk-car" style={{ left: carX - 30, top: carY - 42 }} initial={false}
        animate={{ opacity: carOn ? 1 : 0, y: carOn && driveP < 1 ? [0, -1.5, 0] : 0 }} transition={{ opacity: { duration: 0.4 }, y: { duration: 0.18, repeat: Infinity } }} data-testid="pk-visit-car">
        <span className="pk-car-beam" style={{ opacity: driveP < 1 ? 1 : 0.35 }} />
        <Car size={60} strokeWidth={1.5} />
      </motion.div>
      <motion.div className="pk-abs pk-visit-card" style={{ left: -CARD_W / 2, top: cardTop, width: CARD_W, height: CARD_H }} initial={false}
        animate={{ opacity: photo ? 1 : 0, y: photo ? 0 : 30, scale: photo ? 1 : 0.85 }}
        transition={photo ? { type: 'spring', stiffness: 230, damping: 20 } : { duration: 0.4 }} data-testid="pk-visit-card">
        <img src={photographer.url} alt="" />
        <motion.div className="pk-visit-flash" initial={false} animate={{ opacity: flash ? 1 : 0 }} transition={{ duration: 0.22 }} />
        <div className="pk-visit-tag"><Camera size={14} /> NicheCom on site</div>
        <div className="pk-visit-rec">● REC</div>
      </motion.div>
      <motion.div className="pk-abs pk-drone" style={{ left: -42, top: cardTop - 96 }} initial={false}
        animate={drone ? { opacity: 1, y: [0, -10, 0], x: [0, 8, 0] } : { opacity: 0, y: 70, x: 0 }}
        transition={drone ? { opacity: { duration: 0.6 }, y: { duration: 2.6, repeat: Infinity, ease: 'easeInOut' }, x: { duration: 3.4, repeat: Infinity, ease: 'easeInOut' } } : { duration: 0.5 }} data-testid="pk-visit-drone">
        <DroneOutline />
        <span className="pk-drone-trail" />
        <span className="pk-drone-tag">Drone</span>
      </motion.div>
      <div className="pk-abs pk-visit-chips" style={{ left: -170, top: STEM_DOWN + 18, width: 340 }}>
        {VISIT_CHIPS.map((c, i) => {
          const on = now >= chipsAt + i * 0.22;
          return <motion.span key={c} initial={false} animate={{ opacity: on ? 1 : 0, y: on ? 0 : 14, scale: on ? 1 : 0.8 }} transition={{ duration: 0.4, ease: EASE }}>{c}</motion.span>;
        })}
      </div>
    </>
  );
}

export function Road({ groupTMV, frozenAt, groupT }) {
  const camX = useTransform(groupTMV, (v) => 640 - (X0 + (frozenAt ?? v) * PX));
  const bgX = useTransform(camX, (v) => v * 0.35);
  const markerX = useTransform(groupTMV, (v) => X0 + (frozenAt ?? v) * PX);
  const markerY = useTransform(markerX, (x) => yAt(x) - 22);
  const now = frozenAt ?? groupT;
  return (
    <div className="pk-fill pk-road-root">
      <motion.div className="pk-abs pk-road-parallax" style={{ x: bgX }} />
      <motion.div className="pk-abs pk-road-world" style={{ x: camX }}>
        <svg className="pk-abs" width={ROAD_LEN + 400} height={1080} style={{ left: -400, top: 0 }}>
          <path d={ROAD_PATH} transform="translate(400,0)" fill="none" stroke="rgba(0,0,0,0.35)" strokeWidth={46} strokeLinecap="round" />
          <path d={ROAD_PATH} transform="translate(400,0)" fill="none" stroke="rgba(150,193,31,0.14)" strokeWidth={30} strokeLinecap="round" />
          <path d={ROAD_PATH} transform="translate(400,0)" fill="none" stroke="#96C11F" strokeWidth={3} strokeDasharray="18 22" className="pk-road-dash" />
          {MILESTONES.filter((m) => m.branch).map((m) => {
            const hub = MILESTONES.find((h) => h.hub);
            const x1 = hub.x; const y1 = yAt(hub.x);
            const on = now >= m.time - 0.5;
            const x2 = m.x; const y2 = yAt(m.x) + m.branch * 170;
            return (
              <motion.path key={m.label} d={`M${x1},${y1} C${x1 + 120},${y1} ${x2 - 160},${y2} ${x2},${y2}`} fill="none" stroke="#C6EE5A" strokeWidth={2.5}
                initial={false} animate={{ pathLength: on ? 1 : 0, opacity: on ? 0.9 : 0 }} transition={{ duration: 1.2, ease: EASE }} />
            );
          })}
        </svg>
        {MILESTONES.map((m) => {
          const visible = now >= m.time - 9;
          const active = now >= m.time;
          const y = yAt(m.x) + (m.branch ? m.branch * 170 : 0);
          const up = m.side === 'up';
          const sold = m.sold && now >= 57.2;
          const stem = up ? STEM : STEM_DOWN;
          const hidden = sold ? { opacity: 0, scale: 0.6 } : { opacity: visible ? (active ? 1 : 0.45) : 0, y: visible ? 0 : up ? -40 : 40, scale: active ? 1 : 0.94 };
          return (
            <div key={m.label} className="pk-abs" style={{ left: m.x, top: y }}>
              <motion.div className={`pk-node ${active ? 'pk-node-on' : ''}`} initial={false} animate={{ scale: visible ? 1 : 0 }} transition={{ duration: 0.5, ease: EASE }} />
              {!m.branch && <motion.div className="pk-node-stem" style={{ top: up ? -stem - 14 : 14, height: stem }} initial={false} animate={{ scaleY: active ? 1 : 0 }} transition={{ duration: 0.5, ease: EASE }} />}
              {m.visit ? (
                <VisitMilestone m={m} now={now} photographer={IMAGES.photographer} />
              ) : m.media ? (
                <motion.div className="pk-abs pk-milestone-phone" style={{ left: -PHONE_W / 2, top: up ? -stem - 16 - PHONE_H : stem + 16 }}
                  initial={false} animate={hidden} transition={{ duration: 0.7, ease: EASE }} data-testid={`pk-journey-phone-${m.label.replace(/\s+/g, '-').toLowerCase()}`}>
                  <PhoneFrame width={PHONE_W} glow={active}>
                    <Vid video={m.media} className="pk-fill" active={visible} />
                    <div className="pk-phone-tag">{m.label}</div>
                  </PhoneFrame>
                </motion.div>
              ) : (
                <motion.div
                  className={`pk-glass pk-milestone ${active ? 'pk-glass-active' : ''}`}
                  style={{ top: m.branch ? -46 : up ? -stem - 18 - 72 : stem + 18, left: m.branch ? 30 : -130 }}
                  initial={false} animate={hidden} transition={{ duration: 0.7, ease: EASE }}
                >
                  <div className="pk-milestone-label">{m.label}</div>
                </motion.div>
              )}
            </div>
          );
        })}
        {!frozenAt && <motion.div className="pk-abs pk-marker" style={{ x: markerX, y: markerY }}><span /></motion.div>}
      </motion.div>
    </div>
  );
}

export function Journey({ scene }) {
  const { t, stage, groupT, groupTMV } = useSceneTime();
  const soldBurst = groupT >= 57.2;
  return (
    <div className="pk-fill">
      <Road groupTMV={groupTMV} groupT={groupT} />
      <div className="pk-abs" style={{ left: 120, top: 64, width: 1500 }}>
        <Eyebrow show={t >= 0.3}>{['Scene 11 · Before', 'Scene 12 · Launch', 'Scene 13 · After'][stage]} — one property, one journey</Eyebrow>
        <Words key={stage} text={scene.stageTitle} show={t >= 0.5} hide={soldBurst} size={72} lime={stage === 1 ? [0] : [0, 1, 2]} style={{ marginTop: 6 }} />
        {stage === 0 && <Fade show={t >= 5} y={12} className="pk-sub" style={{ marginTop: 8, fontSize: 26 }}>Content starts before the property even launches.</Fade>}
        {stage === 1 && <Fade show={t >= 4} y={12} className="pk-sub" style={{ marginTop: 8, fontSize: 26 }}>One core asset. Every channel. Automatically branded.</Fade>}
        {stage === 2 && <Fade show={t >= 3 && !soldBurst} y={12} className="pk-sub" style={{ marginTop: 8, fontSize: 26 }}>Through to SOLD — and the next instruction.</Fade>}
      </div>
      <motion.div className="pk-abs pk-sold-burst" style={{ left: 760, top: 240, width: 400, height: 400 }} initial={false}
        animate={{ scale: soldBurst ? 5 : 0, opacity: soldBurst ? [0, 0.6, 0] : 0 }} transition={{ duration: 1.6, ease: 'easeOut' }} />
      <motion.div className="pk-abs pk-sold-reel" style={{ left: 760, top: 150, width: 400 }} initial={false}
        animate={{ opacity: soldBurst ? 1 : 0, scale: soldBurst ? 1 : 0.3, y: soldBurst ? 0 : 200 }} transition={{ duration: 1.1, ease: EASE }}>
        <div className="pk-reel-card">
          <Vid video={VIDEOS.jsSold} className="pk-fill" active={soldBurst} />
          <div className="pk-reel-tag">SOLD</div>
        </div>
      </motion.div>
      <Fade show={t >= 15.5 && stage === 2 && !soldBurst} y={10} className="pk-caption" style={{ position: 'absolute', left: 120, top: 980 }}>ONE PROPERTY → FULL CONTENT LIFECYCLE</Fade>
    </div>
  );
}

// ── 14 THE OBVIOUS PROBLEM ──────────────────────────────────────
const WORK_ICONS = { Camera, Microphone: Mic, 'Editing timeline': Film, 'Ring light': Sun, Phone: Smartphone, 'Social apps': Share2, Scripts: FileText, Notifications: Bell };
export const WORK_POS = [
  { x: 600, y: 470, r: -9 }, { x: 1190, y: 450, r: 7 }, { x: 540, y: 640, r: 5 }, { x: 1250, y: 630, r: -6 },
  { x: 620, y: 810, r: -4 }, { x: 1210, y: 800, r: 8 }, { x: 760, y: 930, r: 6 }, { x: 1060, y: 935, r: -7 },
];

export function WorkChip({ label, style, className = '', ...rest }) {
  const Icon = WORK_ICONS[label] || Camera;
  return (
    <motion.div className={`pk-glass pk-work-chip ${className}`} style={style} {...rest}>
      <Icon size={24} /><span>{label}</span>
      {label === 'Notifications' && <em className="pk-badge">47</em>}
    </motion.div>
  );
}

export function Work({ scene }) {
  const { t, groupTMV } = useSceneTime();
  const frozen = useMemo(() => 57, []);
  const kicker = t >= 7;
  return (
    <div className="pk-fill">
      <div className="pk-fill pk-road-frozen"><Road groupTMV={groupTMV} frozenAt={frozen} groupT={frozen} /></div>
      <div className="pk-abs" style={{ left: 120, top: 150, width: 1680, textAlign: 'center' }}>
        <Words text={scene.quote} show={t >= 0.35} hide={t >= 9} size={104} stagger={0.06} className="pk-words-center" />
        <Fade show={t >= 4 && t < 9} y={14} className="pk-sub" style={{ marginTop: 26, fontSize: 44, fontStyle: 'italic' }}>{scene.beat}</Fade>
        <div style={{ marginTop: t >= 9 ? -140 : 0 }}>
          <Words text={scene.kicker} show={kicker} size={104} className="pk-words-center" lime={[2, 3]} />
        </div>
      </div>
      <motion.div className="pk-abs pk-agent" style={{ left: 880, top: 620 }} initial={false}
        animate={{ opacity: t >= 9 ? 1 : 0, scale: t >= 9 ? 1 : 0.6, rotate: t >= 16 ? [0, -3, 3, -2, 0] : 0 }}
        transition={{ duration: 0.8, ease: EASE, rotate: { duration: 0.8, repeat: Infinity } }}>
        <User size={72} /><div className="pk-agent-label">The agent</div>
      </motion.div>
      {scene.items.map((label, i) => {
        const p = WORK_POS[i];
        const on = t >= 9.5 + i * 0.8;
        return (
          <WorkChip key={label} label={label} style={{ left: p.x, top: p.y }} initial={false}
            animate={{ opacity: on ? 1 : 0, y: on ? 0 : -700, rotate: on ? p.r : -30 }}
            transition={{ type: 'spring', stiffness: 160, damping: 14, mass: 0.9 }} />
        );
      })}
    </div>
  );
}

// ── 15 THAT'S WHY THERE'S AI IN MY JOB TITLE ───────────────────
const OUTPUT_ICONS = [Music, Scissors, Palette, PenLine, AudioLines, UserRound, Layers];

export function AI({ scene }) {
  const { t } = useSceneTime();
  const sucked = t >= 0.6;
  const engine = t >= 1.8;
  return (
    <div className="pk-fill">
      {['Camera', 'Microphone', 'Editing timeline', 'Ring light', 'Phone', 'Social apps', 'Scripts', 'Notifications'].map((label, i) => {
        const p = WORK_POS[i];
        const go = t >= 0.6 + i * 0.22;
        return (
          <WorkChip key={label} label={label} style={{ left: p.x, top: p.y }} initial={false}
            animate={{ x: go ? 960 - p.x - 90 : 0, y: go ? 600 - p.y - 30 : 0, scale: go ? 0.1 : 1, opacity: go ? 0 : 1, rotate: go ? 180 : p.r }}
            transition={{ duration: 1.1, ease: [0.7, 0, 0.84, 0] }} />
        );
      })}
      <motion.div className="pk-abs pk-engine" style={{ left: 960 - 190, top: 600 - 190 }} initial={false}
        animate={{ opacity: engine ? 1 : 0, scale: engine ? (t >= 9 ? 0.72 : 1) : 0.2, x: t >= 9 ? -420 : 0 }} transition={{ duration: 1.3, ease: EASE }}>
        <div className="pk-engine-ring r1" /><div className="pk-engine-ring r2" /><div className="pk-engine-ring r3" />
        <div className="pk-engine-core"><Sparkles size={64} /></div>
      </motion.div>
      <div className="pk-abs" style={{ left: 210, top: 110, width: 1500, textAlign: 'center' }}>
        <Words text={scene.headline} show={t >= 3} size={92} className="pk-words-center" lime={[3]} stagger={0.07} />
        <Fade show={t >= 7.5} y={14} className="pk-sub" style={{ marginTop: 24, fontSize: 40, letterSpacing: '0.02em' }}>{scene.sub}</Fade>
      </div>
      <svg className="pk-abs" width={1920} height={1080} style={{ left: 0, top: 0, pointerEvents: 'none' }}>
        {scene.outputs.map((o, i) => {
          const on = t >= 9.5 + i * 0.75;
          const y = 440 + i * 70;
          return (
            <motion.path key={o} d={`M740,600 C900,600 920,${y} 1080,${y}`} fill="none" stroke="rgba(150,193,31,0.6)" strokeWidth={2}
              initial={false} animate={{ pathLength: on ? 1 : 0, opacity: on ? 1 : 0 }} transition={{ duration: 0.9, ease: EASE }} />
          );
        })}
      </svg>
      {scene.outputs.map((o, i) => {
        const on = t >= 10 + i * 0.75;
        const Icon = OUTPUT_ICONS[i];
        return (
          <motion.div key={o} className="pk-glass pk-glass-active pk-output" style={{ left: 1090, top: 440 + i * 70 - 28 }} initial={false}
            animate={{ opacity: on ? 1 : 0, x: on ? 0 : -40 }} transition={{ duration: 0.7, ease: EASE }}>
            <Icon size={22} /><span>{o}</span>
          </motion.div>
        );
      })}
      <Fade show={t >= 16} y={10} className="pk-caption" style={{ position: 'absolute', left: 0, top: 1000, width: 1920, textAlign: 'center' }}>Show, don't explain — the engine does the work.</Fade>
    </div>
  );
}
